import { memo } from 'react';

import { ActionTableCell, CheckBoxTableCell, DefaultTableCell } from '@components';
import { CellType, FormType } from '@enums';
import { FormHandlers } from '@hooks';
import type { Column, ColumnLayout, DraggableItem, FormMetadata, PaginationProps } from '@types';
import { cn, validateValueEqual } from '@utils';

interface DraggableCellProps<T> extends FormMetadata {
  column: Column;
  item: T;
  pagination?: PaginationProps;
  rowId: number;
  layout: ColumnLayout;
  actionHandlers?: FormHandlers<DraggableItem>;
  formType: FormType;
  colId: number | string;
}

export const DraggableCell = memo(
  <T extends DraggableItem>({
    column,
    item,
    rowId,
    layout,
    actionHandlers,
    endpoint,
    formType,
    colId,
    pagination,
  }: DraggableCellProps<T>) => {
    const cellValue = Array.isArray(column.value)
      ? column.value.map((value) => item[value]).join(', ')
      : item[column.value];

    const renderContent = () => {
      const formattedData = column.format?.(cellValue, item) ?? cellValue;

      switch (column.cellType) {
        case CellType.Action:
          if (!actionHandlers) return null;
          return (
            <ActionTableCell
              formType={formType}
              multiLines={column.multiLines}
              actionHandlers={actionHandlers}
              data={item}
            />
          );
        case CellType.Checkbox:
          if (!actionHandlers) return;
          return <CheckBoxTableCell endpoint={endpoint} data={item} formType={formType} />;
        default:
          return (
            <DefaultTableCell
              pagination={pagination}
              rowId={rowId}
              data={formattedData}
              column={column}
              multiLines={column.multiLines}
            />
          );
      }
    };
    const isActionCell = validateValueEqual(column.cellType, CellType.Action);
    const isCheckboxCell = validateValueEqual(column.cellType, CellType.Checkbox);
    return (
      <div
        className={cn(
          'flex px-3 flex-col',
          column.dotsTextOverflow ? 'truncate' : 'break-words',
          column.multiLines ? 'justify-start h-auto py-3.5' : 'justify-center py-1',
          colId === 0 ? 'px-6' : 'px-3',
        )}
        style={{
          width: `${layout.percentage}%`,
          minWidth: layout.minWidth,
          flex: layout.flex,
        }}
      >
        <div
          className={cn('w-full', {
            'text-center': column.center,
            'text-left': !column.center,
            'py-1.5': !column.multiLines && isActionCell,
            'py-3.5': !column.multiLines && !isActionCell,
            'h-full pt-1.5': column.multiLines && !isActionCell && !isCheckboxCell,
            'h-full pt-1': column.multiLines && isCheckboxCell,
          })}
        >
          {renderContent()}
        </div>
      </div>
    );
  },
);
